import React, {FC, useState, useEffect} from 'react';
import {useFormik} from 'formik';
import {useStoreon} from 'storeon/react';
import cnBind from 'classnames/bind';
import dayjs from 'dayjs';
import {GxButton, GxForm, GxInput, GxRadio} from '@garpix/garpix-web-components-react';
import { toast } from 'react-toastify';
import { Events, State } from '@/interfaces';
import styles from './styles/index.module.scss';
import Radio from '../../../../components/Radio';
import {PAYMENT_RETURN_PAY_ITEMS, RETURN_PAY_TYPES} from '../ModalOrder/const';
import InputNumbers from '../../../../components/InputNumbers';
import ModalLayout from '../../../../components/ModalLayout';
import {returnMoneyDeposit} from '../../helpers';
import Pagination from '../../../../components/Pagination';
import { LIMIT } from '../ModalUser/ModalUserTabPanels/const';


interface IReturnMoneyDeposit {
    flag: boolean;
    onRequestClose: () => void;
    depositMoney: number,
    userInfo: Record<string, any> | null,
}

const ReturnMoneyDeposit: FC<IReturnMoneyDeposit> = ({onRequestClose, flag, depositMoney, userInfo}) => {
    const cn = cnBind.bind(styles);
    const {dispatch, payOrder} = useStoreon<State, Events>('payOrder');
    const [activePage, setActivePage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(payOrder ? payOrder.count : 0);
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {amount:0, comment: '',return_method: null, date: null, payOrderId: 0},
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values) => {
            if (values.return_method === null) {
                return
            }

            try{
                if (!values.payOrderId){
                   toast.error('Выберите платеж');
                   return;
                }
                await returnMoneyDeposit({
                    pay_order_id:values.payOrderId, 
                    refund_amount: values.amount,
                    return_method: values.return_method,
                    comment: values.comment
                });
                onRequestClose();
                dispatch('depositListUser/get',{user_id: userInfo ? userInfo.id : undefined, is_has_resource: true})
            }catch (e) {
                console.log(e);
            }
        }
    });

    const handleChangePage = (page: number) => {
        setActivePage(page);
        dispatch('payOrder/get', {
            customer: userInfo ? userInfo.id : undefined,
            is_has_money_deposit_payment: true,
            page,
            limit: LIMIT,
        });
    };

    const isSubmitDisabled = formik.values.return_method === null || formik.values.amount === 0 || formik.values.payOrderId === 0

    useEffect(() => {
        if (userInfo !== null) {
            dispatch('payOrder/get', {
                customer: userInfo ? userInfo.id : undefined,
                is_has_money_deposit_payment: true,
                limit: LIMIT,
                page: activePage
            });
        }
    }, [userInfo])

    useEffect(()=>{
        const item = payOrder?.results.find((item)=>item.id === formik.values.payOrderId);
        if(item){
            formik.setFieldValue('amount', item.pay_order_deposit_money)
        }
    },[formik.values.payOrderId])

    useEffect(() => {
        if (payOrder && payOrder.count > 0) {
          setTotalPages(payOrder.count);
        } else {
          setTotalPages(1);
        }
    }, [payOrder]);

    return (
      <ModalLayout
        flag={flag}
        onClose={onRequestClose}
      >
          <div className={styles['header']}>
              <h2 className={styles['header--title']}>Возврат средств.</h2>
              <div className={styles['header--info']}>
                  <span>Кому: {userInfo ? userInfo.last_name : ''} {userInfo ? userInfo.first_name : ''} </span>
              </div>
          </div>

        <GxForm onGx-submit={formik.submitForm}>
            <div className={styles['subheader']}>
                <span className={styles['subheader--subtitle']}>Способ возврата:</span>
            </div>
            <div className={styles['actions']}>
                {PAYMENT_RETURN_PAY_ITEMS
                    .filter((item) =>  item.id !== RETURN_PAY_TYPES.MONEY_DEPOSITS)
                    .map((item) => (
                        <Radio
                            key={item.id}
                            name="return_method" className={styles['actions--item']}
                            checked={formik.values.return_method === item.id}
                            onClick={() => formik.setFieldValue('return_method', item.id)}>
                            {item.title}
                        </Radio>
                    ))}
            </div>
            <div className={styles['wrap-group']}>
                <div className={styles['group']}>
                    <InputNumbers
                      label='Сумма возврата:'
                      name="amount"
                      value={formik.values.amount}
                      onGx-change={(e: Record<string, any>) => {
                          formik.setFieldValue('amount', e.target.value);
                      }}
                      className={cn({
                          'group--input': true,
                          'group--input--error': formik.errors.amount
                      })}
                      type='number'
                    />
                    <span className={styles['symbol--price']}/>
                    <p className={styles['group--error']}>{formik.errors.amount}</p>
                </div>
                <div className={styles['group']}>
                    <GxInput
                      label='Примечание:'
                      name="comment"
                      onGx-change={(e: Record<string, any>) => {
                          formik.setFieldValue('comment', e.target.value);
                      }}
                      className={cn(styles['group--input'])}
                    />
                </div>
            </div>
            <div className={styles['certificate']}>
                <div className={styles['subheader']}>
                    <span className={styles['subheader--subtitle']}>Платежи</span>
                </div>
                <div className={cn(styles['table'], styles['table--certificate'])}>
                    <table className={styles['orders']}>
                        <thead>
                        <tr>
                            <th>Номер счета</th>
                            <th>Дата создания счета</th>
                            <th>Депозит руб</th>
                            <th>Оператор СРК</th>
                            <th>Способ оплаты</th>
                        </tr>
                        </thead>
                        <tbody>
                        {payOrder?.results.map((item)=>(
                          <tr key={item.id}>
                              <td>
                                <GxRadio
                                  onClick={() => {
                                      formik.setFieldValue('payOrderId', item.id);
                                  }}
                                  name="payOrderId"
                                  checked={formik.values.payOrderId === item.id} >
                                  № {item.id}
                                </GxRadio>
                              </td>
                              <td>{dayjs(item.created_at).format('DD.MM.YYYY')}</td>
                              <td>{item.pay_order_deposit_money}</td>
                              <td>{item.operator}</td>
                              <td>{item.payment_type}</td>
                          </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles['pagination']}>
                    <Pagination
                        onChange={(page: number) => handleChangePage(page)}
                        total={totalPages}
                        pageSize={ LIMIT }
                        activePage={activePage}
                    />
                </div>
            </div>
            <div className={styles['footer']}>
                <div className={styles['footer--left-block']}>
                    <div className={styles['pay-info']}>
                        <div className={cn(styles['pay-info__item'], styles['pay-info__item_paid'])}>

                              <p className={styles['pay-info__item_label']}>Ден. депозит:</p>
                              <p>
                                  {depositMoney} ₽
                              </p>
                        </div>
                    </div>
                </div>
                <div className={styles['footer--right-block']}>
                    <GxButton className={styles['btn--gray']} onClick={onRequestClose}>
                        Отмена
                    </GxButton>
                    <GxButton className={styles['btn--default']} type='submit' disabled={isSubmitDisabled}>
                        Вернуть
                    </GxButton>
                </div>
            </div>
        </GxForm>
        </ModalLayout>
    );
};

export default ReturnMoneyDeposit;
